// Generated from scripts/theme-drawer.ts. Rebuild with pnpm scripts:build.
var _a;
import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { isClickedOutside, lockScroll, onAnimationEnd, unlockScroll } from '@theme/utilities';
import { getScrollTop, scrollTo } from '@theme/scroll-container';
import { matchOpenerFocusRing } from '@theme/dialog';
/** Viewport width below which the drawer opens as a modal overlay (no squeeze). */
const MODAL_BREAKPOINT = 990;
/**
 * A drawer that opens from the inline end.
 *
 * On wide viewports (≥ 990px) the drawer squeezes page content alongside it.
 * The panel is a non-modal dialog (`show()`); a focus trap from `trapFocus()`
 * cycles Tab within the drawer, mirroring the modal-mode a11y contract. Focus
 * moves to the close button on open and returns to the trigger on close.
 *
 * On narrow viewports (< 990px), and at every width when the element carries
 * the `modal` attribute, the drawer overlays the page with a backdrop. The panel
 * is then a modal dialog (`showModal()`): native focus trap, scroll lock and
 * ARIA semantics, with the same focus moves.
 *
 * Dispatches {@link DrawerOpenEvent} and {@link DrawerCloseEvent}.
 */
export class ThemeDrawer extends Component {
    requiredRefs = ['panel'];
    /** Monotonically increasing counter for z-index stacking of multiple open drawers. */
    static #stackOrder = 0;
    #isClosing = false;
    /** When set, the drawer reopens after the current close animation finishes. */
    #deferredOpen = false;
    /** Element to restore focus to when the drawer closes. */
    #previouslyFocused = null;
    #modalQuery = window.matchMedia(`(max-width: ${MODAL_BREAKPOINT - 1}px)`);
    get #panel() {
        return this.refs.panel;
    }
    /** Whether the drawer is currently open. */
    get isOpen() {
        return this.hasAttribute('open');
    }
    /** Whether the drawer currently opens as a modal overlay rather than a squeezing sidebar. */
    get #isModal() {
        return this.hasAttribute('modal') || this.#modalQuery.matches;
    }
    connectedCallback() {
        super.connectedCallback();
        this.#modalQuery.addEventListener('change', this.#onModalBreakpointChange);
        // Sync the static stack counter with any --drawer-stack-order set by the
        // synchronous restore script (which runs before this module loads).
        const restoredOrder = Number(this.style.getPropertyValue('--drawer-stack-order') || 0);
        if (restoredOrder > _a.#stackOrder) {
            _a.#stackOrder = restoredOrder;
        }
        if (this.isOpen) {
            this.#onRestore();
        }
    }
    /**
     * Restore path: the inline script in theme-drawer.liquid set [open] on
     * <theme-drawer> + <dialog> before this module loaded. The dialog is
     * already visible, so only the close listeners are wired. Focus is not
     * trapped or moved: the shopper is loading a fresh page and expects focus
     * on main content, not inside a drawer left over from the previous page.
     */
    #onRestore() {
        if (this.#isModal) {
            lockScroll(this.#panel);
        }
        document.addEventListener('keydown', this.#onKeyDown);
        this.#panel.addEventListener('click', this.#onBackdropClick);
    }
    disconnectedCallback() {
        super.disconnectedCallback();
        unlockScroll(this.#panel);
        this.#modalQuery.removeEventListener('change', this.#onModalBreakpointChange);
        this.#removeEventListeners();
        removeTrapFocus();
        this.#deferredOpen = false;
    }
    /**
     * Closes the drawer when the shopper clicks the backdrop (outside the dialog
     * content area). Only meaningful in modal mode; a sidebar has no backdrop.
     */
    #onBackdropClick = (event) => {
        if (!isClickedOutside(event, this.#panel))
            return;
        if (this.#getOpenNestedDialog())
            return;
        this.close();
    };
    /** The open dialog nested inside the drawer panel, if any. */
    #getOpenNestedDialog() {
        return this.#panel.querySelector('dialog[open]');
    }
    /**
     * Switches the dialog between modal and non-modal when the viewport
     * crosses the modal breakpoint while the drawer is open. A drawer with the
     * `modal` attribute is modal at every width and never switches.
     */
    #onModalBreakpointChange = () => {
        if (!this.isOpen || this.hasAttribute('modal'))
            return;
        const panel = this.#panel;
        const nestedDialog = this.#getOpenNestedDialog();
        const activeElement = document.activeElement;
        const nestedActiveElement = nestedDialog?.contains(activeElement) && activeElement instanceof HTMLElement ? activeElement : null;
        const focusShowedRing = activeElement?.matches(':focus-visible') ?? false;
        // Close the current dialog mode and immediately reopen in the new mode.
        // No animation: the drawer stays visually in place.
        panel.close();
        removeTrapFocus();
        if (this.#modalQuery.matches) {
            lockScroll(panel);
            panel.showModal();
        }
        else {
            unlockScroll(panel);
            panel.show();
            trapFocus(panel);
        }
        if (nestedDialog?.open) {
            this.#bringNestedDialogToFront(nestedDialog, nestedActiveElement);
        }
        else {
            this.#focusInitialElement(focusShowedRing);
        }
        // The sessionStorage restore path bypasses open(), so these may not have
        // been added yet. addEventListener deduplicates identical listeners.
        document.addEventListener('keydown', this.#onKeyDown);
        panel.addEventListener('click', this.#onBackdropClick);
    };
    /**
     * Closes the drawer when the shopper presses Escape.
     * preventDefault() suppresses the native dialog cancel event in modal mode.
     */
    #onKeyDown = (event) => {
        if (event.key !== 'Escape')
            return;
        // isComposing: an Input Method Editor consumes Escape to dismiss its
        // candidate list, which can happen inside the cart's discount-code field.
        // defaultPrevented: nested components handle Escape first.
        if (event.isComposing || event.defaultPrevented)
            return;
        if (!this.isOpen || !this.#isTopmost())
            return;
        // An open popover inside the panel (the cart's Change options) closes on Escape by itself, and the browser does
        // that after this listener has run, so the drawer would close with it. Escape is the popover's first.
        if (this.#hasOpenPopover())
            return;
        event.preventDefault();
        this.close();
    };
    /** Whether a popover inside the panel is open. */
    #hasOpenPopover() {
        return Array.from(this.#panel.querySelectorAll('[popover]')).some((popover) => popover.matches(':popover-open'));
    }
    /**
     * Shows the drawer.
     *
     * @param options.enter - `'inline-start'` slides left-to-right (used when the chat docks).
     */
    open({ enter } = {}) {
        if (this.isOpen) {
            this.#bringToFront({ enter });
            return;
        }
        if (this.#isClosing) {
            this.#deferredOpen = { enter };
            return;
        }
        const panel = this.#panel;
        _a.#stackOrder += 1;
        this.style.setProperty('--drawer-stack-order', String(_a.#stackOrder));
        const openClass = this.#openClass(enter);
        panel.classList.add(openClass);
        this.#previouslyFocused = document.activeElement instanceof HTMLElement ? document.activeElement : null;
        const openerShowedRing = this.#previouslyFocused?.matches(':focus-visible') ?? false;
        if (this.#isModal) {
            lockScroll(panel);
            panel.showModal();
        }
        else {
            panel.show();
            trapFocus(panel);
        }
        this.#focusInitialElement(openerShowedRing);
        onAnimationEnd(panel, () => panel.classList.remove(openClass), { subtree: false });
        this.setAttribute('open', '');
        this.dispatchEvent(new DrawerOpenEvent());
        document.addEventListener('keydown', this.#onKeyDown);
        panel.addEventListener('click', this.#onBackdropClick);
    }
    #openClass(enter) {
        return enter === 'inline-start' ? 'theme-drawer__dialog--opening-inline-start' : 'theme-drawer__dialog--opening';
    }
    /**
     * Bumps this drawer's z-index to the top of the stack and replays the
     * slide-in animation. Called when open() is invoked on an open drawer.
     */
    #bringToFront({ enter } = {}) {
        const panel = this.#panel;
        _a.#stackOrder += 1;
        this.style.setProperty('--drawer-stack-order', String(_a.#stackOrder));
        // Modal dialogs live in the top layer, where z-index is ignored and
        // stacking follows showModal() call order. Re-calling showModal() moves
        // this dialog to the top of the stack.
        if (this.#isModal && panel.open) {
            lockScroll(panel);
            panel.close();
            panel.showModal();
        }
        const openClass = this.#openClass(enter);
        panel.classList.add(openClass);
        onAnimationEnd(panel, () => panel.classList.remove(openClass), { subtree: false });
        // Re-fire so persistDrawerState picks up the new stack order.
        this.dispatchEvent(new DrawerOpenEvent());
    }
    #removeEventListeners() {
        document.removeEventListener('keydown', this.#onKeyDown);
        this.#panel.removeEventListener('click', this.#onBackdropClick);
    }
    /**
     * Moves focus to the close button inside the drawer panel. Every drawer
     * renders one with the `theme-drawer__close-button` class; a drawer that
     * omits it owns its own focus management. The ring follows the opener: a
     * keyboard user sees it, a tap or a script (an add to cart) does not.
     *
     * @param openerShowedRing - Whether the element focused before the drawer
     *   opened showed its focus ring.
     */
    #focusInitialElement(openerShowedRing) {
        this.#panel.querySelector('.theme-drawer__close-button')?.focus();
        matchOpenerFocusRing(openerShowedRing);
    }
    /**
     * Reopens a nested modal after the drawer panel changes mode so it remains
     * above the drawer in the browser top layer.
     */
    #bringNestedDialogToFront(dialog, focusTarget) {
        dialog.close();
        dialog.showModal();
        if (focusTarget && document.contains(focusTarget) && dialog.contains(focusTarget)) {
            focusTarget.focus();
        }
    }
    /** Slides the drawer out, waits for the animation, then closes the dialog. */
    async #closeDialog() {
        if (!this.isOpen || this.#isClosing)
            return;
        this.#isClosing = true;
        this.#removeEventListeners();
        removeTrapFocus();
        const panel = this.#panel;
        // A modal drawer scroll-locks the page, so the shopper's position cannot
        // legitimately change while it is open. Releasing that lock, closing the
        // dialog and restoring focus can each move the root scroller, so the
        // offset is captured up front and re-applied once the drawer is gone.
        const closingAsModal = this.#isModal;
        const scrollTopWhileLocked = closingAsModal ? getScrollTop() : null;
        this.removeAttribute('open');
        this.dispatchEvent(new DrawerCloseEvent());
        unlockScroll(panel);
        if (panel.open) {
            // Cancel any in-progress open animation before starting the close.
            panel.classList.remove('theme-drawer__dialog--opening', 'theme-drawer__dialog--opening-inline-start');
            panel.classList.add('theme-drawer__dialog--closing');
            await onAnimationEnd(panel, undefined, { subtree: false });
            panel.classList.remove('theme-drawer__dialog--closing');
        }
        panel.close();
        this.style.removeProperty('--drawer-stack-order');
        if (!document.querySelector('theme-drawer[open]')) {
            _a.#stackOrder = 0;
        }
        const trigger = this.#previouslyFocused;
        this.#previouslyFocused = null;
        // A Section Rendering API morph between open and close can replace the
        // trigger node, and focus() on a detached node is a silent no-op, so a
        // detached trigger falls back to a fresh query. preventScroll only on a
        // modal close: there the captured offset is re-applied below. A sidebar
        // close locks no scroll, so the browser should bring the restored focus
        // target into view.
        if (trigger && document.contains(trigger)) {
            trigger.focus({ preventScroll: closingAsModal });
        }
        else {
            document.querySelector(`[aria-controls="${this.id}"]`)?.focus({ preventScroll: closingAsModal });
        }
        if (scrollTopWhileLocked != null && getScrollTop() !== scrollTopWhileLocked) {
            scrollTo({ top: scrollTopWhileLocked, behavior: 'instant' });
        }
        this.#isClosing = false;
        // Reconcile: if open() was called during the close animation, reopen now.
        if (this.#deferredOpen) {
            const options = this.#deferredOpen;
            this.#deferredOpen = false;
            this.open(options);
        }
    }
    /** Closes the drawer. */
    async close() {
        if (this.#isClosing) {
            // A close is already in progress: cancel any deferred open so the
            // drawer stays closed when the animation finishes.
            this.#deferredOpen = false;
            return;
        }
        await this.#closeDialog();
    }
    /**
     * Toggles the drawer. An open drawer that is not the topmost in the stack
     * is brought to front instead of closed.
     */
    toggle() {
        if (this.isOpen && this.#isTopmost()) {
            return this.close();
        }
        return this.open();
    }
    /** Whether this drawer has the highest stack order among all open drawers. */
    #isTopmost() {
        const myOrder = Number(this.style.getPropertyValue('--drawer-stack-order') || 0);
        for (const other of document.querySelectorAll('theme-drawer[open]')) {
            if (other === this)
                continue;
            const otherOrder = Number(other.style.getPropertyValue('--drawer-stack-order') || 0);
            if (otherOrder > myOrder)
                return false;
        }
        return true;
    }
}
_a = ThemeDrawer;
if (!customElements.get('theme-drawer')) {
    customElements.define('theme-drawer', ThemeDrawer);
}
export class DrawerOpenEvent extends CustomEvent {
    constructor() {
        super(DrawerOpenEvent.eventName, { bubbles: true });
    }
    static eventName = 'theme-drawer:open';
}
export class DrawerCloseEvent extends CustomEvent {
    constructor() {
        super(DrawerCloseEvent.eventName, { bubbles: true });
    }
    static eventName = 'theme-drawer:close';
}
